import React, {
  useEffect, useState, useRef, useContext, useMemo
} from 'react';
import proptypes from 'prop-types';
const { string, func, shape } = proptypes;
import {
  useDataModel,
  hoist,
  extend,
  arrayOf,
  customType,
  params,
  shape as shapeType,
  string as stringType,
  bool as boolType,
  QueryProvider,
  QueryContext
} from '@thd-nucleus/data-sources';
import {
  Skeleton, SkeletonContent, SkeletonLine, SkeletonBlock
} from '@one-thd/sui-atomic-components';
import { useStore } from '@thd-nucleus/experience-context';
import { Carousel } from '@one-thd/sui-carousel';
import { AddToContext } from '../../AddToContext';
import { BoughtTogetherPod } from './BoughtTogetherPod';
import { useUpdateList, updateListModel } from '../../hooks/useLists';
import { OPERATION_ADD_ITEMS, SCREENS } from '../../AddToConstants';
import { getAddToListMessage, getTotalPrice } from '../../../utils/boughtTogether.utils';
import PriceMessage from './PriceMessage';

const HoistedCarousel = hoist(Carousel, BoughtTogetherPod);

const BoughtTogetherCarouselComponent = ({
  itemId,
  membershipInformation,
  taxonomyData,
  onRecsLoad,
  listId, // Optional - when provided, enables add-to-list functionality
  onItemsAdded // Callback when items are successfully added
}) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const addingItemsRef = useRef([]);
  const lastProcessedUpdateRef = useRef(null);
  const { isClientResolved } = useContext(QueryContext);

  // Create a stable reference for onItemsAdded callback to prevent stale closures
  const onItemsAddedRef = useRef(onItemsAdded);
  useEffect(() => {
    onItemsAddedRef.current = onItemsAdded;
  }, [onItemsAdded]);

  const { setScreen, storeId: storeIdContext } = useContext(AddToContext);
  const { storeId: storeIdFromHook } = useStore();
  const storeId = storeIdContext || storeIdFromHook;

  const { updateList, updateListResponse } = useUpdateList(
    {
      id: listId || null,
      operationName: OPERATION_ADD_ITEMS
    }
  );

  const recsopts = {
    variables: {
      anchorId: itemId,
      storeId,
      recsContext: taxonomyData,
      isAnchorFirstResult: true,
      apiName: 'fbt_test',
      loyaltyMembershipInput: membershipInformation,
      serviceType: null,
      isBrandPricingPolicyCompliant: true
    },
    skip: !itemId,
    ssr: false
  };

  const {
    data: fallBackRecsData,
    loading: fallBackRecsLoading,
    error: fallBackRecsError
  } = useDataModel('recs', recsopts);

  const skipFn = ({ skip, queryName, attributes }) => {
    if (queryName !== 'product') {
      return skip;
    }
    const isResolved = isClientResolved({ queryName: 'products' });
    if (attributes.product?.fulfillment) {
      if (isResolved) {
        return skip;
      }
      return true;
    }
    return skip;
  };

  useEffect(() => {
    if (fallBackRecsData && onRecsLoad) {
      onRecsLoad(fallBackRecsData);
    }
  }, [fallBackRecsData, onRecsLoad]);

  // Handle item selection changes from BoughtTogetherPod components
  const handleSelectionChange = ({ itemId: productItemId, checked }) => {
    setSelectedItems((prevSelected) => {
      if (checked) {
        // Add item to selection
        return [...prevSelected, { itemId: productItemId, quantity: 1 }];
      }
      // Remove item from selection
      return prevSelected.filter((item) => item.itemId !== productItemId);
    });
  };

  // Handle adding selected items to list
  const handleAddToList = async () => {
    if (!listId || selectedItems.length === 0) {
      return;
    }

    // Store the items being added in ref to avoid dependency cycle
    addingItemsRef.current = [...selectedItems];

    try {
      await updateList({
        variables: {
          products: selectedItems
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error adding items to list:', error);
    }
  };

  // Handle successful add to list
  useEffect(() => {
    const currentUpdateId = updateListResponse?.data?.updateList?.id;

    if (
      currentUpdateId
      && !updateListResponse.loading
      && lastProcessedUpdateRef.current !== currentUpdateId
    ) {
      lastProcessedUpdateRef.current = currentUpdateId;

      const addedItems = addingItemsRef.current;
      if (onItemsAddedRef.current && addedItems.length > 0) {
        onItemsAddedRef.current(addedItems);
      }
      // Reset selected items after successful addition
      setSelectedItems([]);
      addingItemsRef.current = [];
      // Navigate to default screen only after successful addition
      setScreen(SCREENS.DEFAULT);
    }
  }, [updateListResponse?.data?.updateList?.id, updateListResponse?.loading, setScreen]);

  // Carousel breakpoint configuration
  // sm-xl breakpoints use the same configuration to maintain consistent 2-item layout
  // across tablets and smaller desktop screens for optimal product visibility
  const defaultBreakpointConfig = {
    slidesPerView: 2,
    slidesPerGroup: 1,
    spaceBetween: 8
  };

  const breakpoints = {
    sm: defaultBreakpointConfig,
    md: defaultBreakpointConfig,
    lg: defaultBreakpointConfig,
    xl: defaultBreakpointConfig,
    '2xl': {
      slidesPerView: 3,
      slidesPerGroup: 1,
      spaceBetween: 8
    }
  };

  // Filter out the first product (anchor item) since we don't want to show it
  const filteredProducts = fallBackRecsData?.recs?.products
    ? fallBackRecsData.recs.products.filter(
      (productWrapper) => productWrapper?.product?.identifiers?.itemId
        && productWrapper.product.identifiers.itemId !== itemId
    )
    : [];

  // Calculate pricing based on selected items and filtered products
  const pricing = useMemo(() => {
    const checkedProducts = filteredProducts
      .map((productWrapper) => {
        const product = productWrapper?.product;
        if (typeof product?.identifiers?.itemId !== 'undefined') {
          return {
            ...product,
            checked: selectedItems.some((item) => item.itemId === product.identifiers.itemId)
          };
        }
        return null;
      })
      .filter(Boolean);
    return getTotalPrice(checkedProducts);
  }, [selectedItems, filteredProducts]);

  // Show loading state
  if (fallBackRecsLoading) {
    return (
      <div className="sui-flex">
        {
          // eslint-disable-next-line id-length
          Array.from({ length: 5 }, (_, index) => (
            <div className="sui-w-full" key={`intent-rec-placeholder${index}`}>
              <Skeleton
                orientation="vertical"
                grow
                density="loose"
              >
                <SkeletonContent disablePadding orientation="horizontal" density="loose">
                  <SkeletonContent density="loose" disablePadding initialSize="2/4">
                    <SkeletonBlock aspect="square" />
                  </SkeletonContent>
                  <SkeletonContent disablePadding density="loose" initialSize="2/4">
                    <SkeletonLine fullWidth numberOfLines={3} />
                  </SkeletonContent>
                </SkeletonContent>
                <SkeletonContent disablePadding grow density="loose">
                  <SkeletonLine fullWidth variant="single" />
                </SkeletonContent>
              </Skeleton>
            </div>
          ))
        }
      </div>
    );
  }

  // Show error state or no data
  if (fallBackRecsError || filteredProducts.length === 0) {
    return null;
  }

  return (
    <QueryProvider
      cacheKey="bought-together-carousel"
      dataSource="rex-api"
      dataSources="rex-api"
      skip={skipFn}
      defaultVariables={{
        skipInstallServices: true,
        storeId,
        loyaltyMembershipInput: membershipInformation,
        isBrandPricingPolicyCompliant: true
      }}
    >
      <HoistedCarousel
        slidesPerView={2}
        slidesPerGroup={1}
        breakpoints={breakpoints}
        peek="md"
        scrollbarPosition="inside"
        layout="full-bleed"
        containerId="bought-together-carousel"
        data-testid="carousel"
      >
        {filteredProducts.map((productWrapper, index) => {
          const product = productWrapper?.product;
          return (
            <BoughtTogetherPod
              key={product?.identifiers?.itemId || index}
              itemId={product?.identifiers?.itemId}
              storeId={storeId}
              podIndex={index}
              isSelected={selectedItems.some((item) => item.itemId === product?.identifiers?.itemId)}
              onSelectionChange={handleSelectionChange}
            />
          );
        })}
      </HoistedCarousel>
      <PriceMessage
        itemId={itemId}
        storeId={storeId}
        message={getAddToListMessage(selectedItems.length)}
        onAddToListClick={handleAddToList}
        pricing={pricing}
        listId={listId}
        loading={updateListResponse?.loading}
      />
    </QueryProvider>
  );
};

BoughtTogetherCarouselComponent.propTypes = {
  itemId: string.isRequired,
  membershipInformation: shape({
    svocID: string,
    programTiers: shape({
      tier: string,
      program: string
    })
  }),
  taxonomyData: shape({
    category: string,
    subcategory: string,
    productType: string
  }),
  onRecsLoad: func,
  listId: string, // Optional - when undefined, add-to-list functionality is disabled
  onItemsAdded: func
};

BoughtTogetherCarouselComponent.defaultProps = {
  membershipInformation: undefined,
  taxonomyData: undefined,
  onRecsLoad: undefined,
  listId: undefined,
  onItemsAdded: undefined
};

const recsContextSchema = customType('RecsContext').shape({
  category: stringType(),
  subcategory: stringType(),
  productType: stringType()
});

// Wrapped product schema for recommendations
const wrappedProduct = customType('WrappedProduct').shape({
  strategy: stringType(),
  isAnchor: boolType(),
  product: params({
    dataSource: stringType()
  })
    .shape(BoughtTogetherPod.dataModel.product)
});

// Define the dataModel for the recs query
BoughtTogetherCarouselComponent.dataModel = extend({
  recs: params({
    anchorId: stringType().isRequired(),
    apiName: stringType().isRequired(),
    storeId: stringType(),
    isAnchorFirstResult: boolType(),
    loyaltyMembershipInput: customType('LoyaltyMembershipInput').shape({
      svocID: stringType(),
      programTiers: arrayOf(shapeType({
        tier: stringType(),
        program: stringType()
      }))
    }),
    serviceType: stringType(),
    mcvisId: stringType(),
    timeStamp: stringType(),
    appId: stringType(),
    key: stringType(),
    nvalue: stringType(),
    maxResults: stringType(),
    recsContext: recsContextSchema
  }).shape({
    metadata: shapeType({
      apiName: stringType(),
      title: stringType(),
      version: stringType(),
      modelName: stringType(),
      fallbackModelName: stringType(),
      type: stringType()
    }),
    products: arrayOf(wrappedProduct)
  })
}, BoughtTogetherPod, updateListModel);

export const BoughtTogetherCarousel = hoist(BoughtTogetherCarouselComponent);
